import { ACTION_STATUSES, FontLocation, LANDING_PATH } from 'shared/consts';
import { EXCHANGE_V2_INTEGRATIONS } from 'utils/native-enabled-exchange';
import { ThemeLocationFont, useThemeFont } from 'hooks/use-theme-font.hook';
import { getInfiniteResources, getResources, resetInfiniteResourcesState } from 'store/resources/actions';
import { resourcesState } from 'store/resources/selectors';
import { selectAppInstanceConfigsStatus, selectCurrentLocationId } from 'store/app-instance-configs/selectors';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FilterParams } from 'store/resources/types';
import { Resource } from 'store/resource/types';
import { getFilterParams } from 'utils';
import { useLocation } from 'react-router-dom';
import { useMiniappSdk } from 'components/miniapp-sdk-provider/miniapp-sdk.hook';
import { useOwnerParams } from 'hooks/use-owner-params.hook';
import { useSearchParams } from 'hooks/use-search-params.hook';
import { useTimeZone } from 'hooks/use-timezone.hook';
import { HEADER_BUTTON_MODE } from '@hqo/hqo-miniapp-client-sdk';
import { useFilteringEnabled } from 'hooks/use-filtering-enabled.hook';
import { useTermsAndConditionsVisibility } from 'hooks/use-terms-and-conditions-visibility.hook';
import { useResetResourceState } from 'pages/resources/hooks/useResetResourceState.hook';
import { useResourceLoadingHandler } from 'pages/resources/hooks/resourceLoadingHandler.hook';
import { useAppInstanceConfigFeature } from 'hooks/use-app-instance-config-feature.hook';
import { useIsMapViewEnabled } from 'hooks/use-is-map-view-enabled.hook';
import { useIsSmallViewportWidth } from 'hooks/use-is-small-viewport-width.hook';
import { getTransactions } from 'store/transactions/actions';
import { selectTransactions, selectTransactionsStatus } from 'store/transactions/selectors';
import { getDesktopOffsetElements } from 'pages/resources/utils/getDesktopOffsetElements';
import { useFlags } from 'launchdarkly-react-client-sdk';

interface UseResourcesPageReturnValues {
  resources: Array<Resource>;
  isLoadingState: boolean;
  themeFontTitle: ThemeLocationFont;
  isCompactUiEnabled: boolean;
  shouldShowMapView: boolean;
  shouldShowResourcesSwipeModal: boolean;
  shouldShowFloorPlanFooter: boolean;
  isMobileDevice: boolean;
}

const EXTRA_CONTENT_HEIGHT = 150;
const ELEMENT_HEIGHT = 84;

export const useResourcesPage = (): UseResourcesPageReturnValues => {
  const isMobileDevice = useIsSmallViewportWidth();
  useTermsAndConditionsVisibility();
  const dispatch = useDispatch();
  const timeZone = useTimeZone();
  const { pathname } = useLocation();
  const { ownerType, ownerUuid } = useOwnerParams();
  const { ...queryParams } = useSearchParams();
  const { showFloorPlan, resourceBookingSkipLandingPage, exchangeIntegrationV2 } = useFlags();

  const {
    filterByType,
    filterByCollection,
    startDate,
    startTime,
    endTime,
    duration,
    capacity,
    presetWindows,
    filterByFloor,
    startDates,
    availableNow,
    favoritesOnly,
  } = queryParams;

  const { resources } = useSelector(resourcesState);
  const themeFontTitle = useThemeFont(FontLocation.TITLE);
  const locationId = useSelector(selectCurrentLocationId);
  const appInstanceConfigsStatus = useSelector(selectAppInstanceConfigsStatus);
  const getTransactionsStatus = useSelector(selectTransactionsStatus);
  const isFilteringEnabled = useFilteringEnabled();
  const { isCompactUiEnabled, isNativeAdapter } = useAppInstanceConfigFeature();
  // Not the raw config answer: the floorplan redesign replaces this view — see `useIsMapViewEnabled`.
  const isMapViewEnabled = useIsMapViewEnabled();
  const isLoadingState = useResourceLoadingHandler(isFilteringEnabled);
  const client = useMiniappSdk();
  const transactions = useSelector(selectTransactions);
  const shouldDispatchGetResources = useMemo(
    () =>
      (resourceBookingSkipLandingPage && (pathname === LANDING_PATH || pathname === '/resource-booking')) ||
      pathname === '/resource-booking/resources',
    [pathname, resourceBookingSkipLandingPage],
  );

  const isAppInstanceConfigsReady = appInstanceConfigsStatus === ACTION_STATUSES.FULFILLED;

  const shouldShowResourcesSwipeModal = useMemo(
    () => isAppInstanceConfigsReady && isMapViewEnabled && isMobileDevice && !isLoadingState,
    [isAppInstanceConfigsReady, isMapViewEnabled, isMobileDevice, isLoadingState],
  );

  const shouldShowFloorPlanFooter = useMemo(() => showFloorPlan && !isLoadingState, [showFloorPlan, isLoadingState]);

  const shouldShowMapView = useMemo(
    () => isAppInstanceConfigsReady && isMapViewEnabled && !isLoadingState,
    [isAppInstanceConfigsReady, isLoadingState, isMapViewEnabled],
  );

  /*
   * The mobile map view holds the page still while the swipe modal is dragged over it, so it takes
   * the body's scroll — and has to give it back.
   *
   * Releasing it is not housekeeping for unmount only: the effective map view can turn off while this
   * page stays mounted, because the floorplan redesign replaces it as soon as the building's
   * floorplans load (see `useIsMapViewEnabled`). Locking without a release left the list and the map
   * that took over unable to scroll for the rest of the session.
   */
  useEffect(() => {
    if (!isMobileDevice || !isMapViewEnabled) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobileDevice, isMapViewEnabled]);

  const offsetElements = useMemo<number>(() => {
    if (isMobileDevice) {
      return Math.round((window.innerHeight - EXTRA_CONTENT_HEIGHT) / ELEMENT_HEIGHT);
    }

    return getDesktopOffsetElements();
  }, [isMobileDevice]);

  useEffect(() => {
    client?.header.showHeader();
    client?.header.setHeader(undefined, HEADER_BUTTON_MODE.HISTORY);
    client?.swipe.enableBackSwipe();
  }, [client]);

  useResetResourceState();

  useEffect(() => {
    const searchParams = {
      types: filterByType,
      collection_uuids: filterByCollection,
      startDate,
      startTime,
      endTime,
      duration,
      capacity,
      presetWindows,
      startDates,
      floor: filterByFloor,
      availableNow,
      favorites_only: favoritesOnly === 'true',
    } as FilterParams;

    const filterParams = {
      ...getFilterParams({ ...searchParams }),
      ...(exchangeIntegrationV2 ? { integrations: EXCHANGE_V2_INTEGRATIONS } : {}),
    };

    if (
      shouldDispatchGetResources &&
      ownerType &&
      ownerUuid &&
      appInstanceConfigsStatus === ACTION_STATUSES.FULFILLED &&
      locationId
    ) {
      if (isCompactUiEnabled) {
        dispatch(resetInfiniteResourcesState());
        dispatch(
          getInfiniteResources.request({
            ownerType,
            ownerUuid,
            filterParams: { ...filterParams, limit: offsetElements },
            indexOfSet: 1,
          }),
        );
      } else {
        dispatch(
          getResources.request({
            ownerType,
            ownerUuid,
            filterParams: { ...filterParams, ...(isNativeAdapter ? { discounts: true } : {}) },
          }),
        );
      }
    }
  }, [
    isNativeAdapter,
    shouldDispatchGetResources,
    ownerType,
    filterByType,
    filterByCollection,
    startDate,
    startTime,
    endTime,
    duration,
    capacity,
    timeZone,
    dispatch,
    appInstanceConfigsStatus,
    locationId,
    isCompactUiEnabled,
    pathname,
    ownerUuid,
    presetWindows,
    filterByFloor,
    startDates,
    offsetElements,
    availableNow,
    favoritesOnly,
    exchangeIntegrationV2,
  ]);

  useEffect(() => {
    if (
      appInstanceConfigsStatus === ACTION_STATUSES.FULFILLED &&
      getTransactionsStatus !== ACTION_STATUSES.PENDING &&
      getTransactionsStatus !== ACTION_STATUSES.FULFILLED &&
      locationId &&
      !transactions?.length &&
      resourceBookingSkipLandingPage
    ) {
      dispatch(getTransactions.request({}));
    }
  }, [
    appInstanceConfigsStatus,
    dispatch,
    locationId,
    transactions,
    getTransactionsStatus,
    resourceBookingSkipLandingPage,
  ]);

  return {
    resources,
    isLoadingState,
    themeFontTitle,
    isCompactUiEnabled,
    shouldShowMapView,
    shouldShowResourcesSwipeModal,
    shouldShowFloorPlanFooter,
    isMobileDevice,
  };
};
